/**
 * BriefTab Component
 * TASK-395: Preparation Screen — AI Chat + Tabbed Panel
 * TASK-401: Brief AI Editing via Chat (Apply/Skip UX, Option B)
 *
 * When the AI proposes a brief section update, shows the proposed content
 * inline with Apply / Skip buttons — matching the KYC copilot UX pattern.
 * The section is only saved to the database when the advisor clicks Apply.
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  FileText,
  Edit3,
  Check,
  X,
  RefreshCw,
  Loader2,
  AlertCircle,
  Sparkles,
  Download,
  Eye,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { meetingApi, BriefVersionConflictError } from '../../../services/api/meetingApi';
import {
  getBriefPptxDownloadHref,
  type ExportResponse,
} from '../../../services/api/exportApi';
import { useMeetingPrepBridge } from '../../../stores/meetingPrepBridge';
import { getDockChatStore } from '../../../stores/dockChatStores';
import { useAccount } from '../../../contexts/AccountContext';
import type { BriefUpdateItem } from '../../../types/prepChat.types';
import { selectBrief } from '../../../services/briefWizard/briefData';
import BriefDeckPreview from './BriefDeckPreview';

// ============================================================================
// Types
// ============================================================================

interface BriefTabProps {
  meetingId: string;
  clientName: string;
  meetingType: string;
  briefData: Record<string, unknown> | null;
  isLoading: boolean;
  onRefresh: () => void;
  onSectionSaved?: () => void;
  // TASK-406: deck preview state owned by PreparationPage so it survives
  // tab switches and is the single source of truth for the debounce flow.
  previewExport: ExportResponse | null;
  previewIsRegenerating: boolean;
  previewLanguage: 'en' | 'de' | 'fr';
  onPreviewLanguageChange: (lang: 'en' | 'de' | 'fr') => void;
  onPreviewRegenerate: () => void;
}

interface BriefSection {
  key: string;
  title: string;
  content: string;
}

// ============================================================================
// Component
// ============================================================================

const BriefTab: React.FC<BriefTabProps> = ({
  meetingId,
  clientName,
  meetingType,
  briefData,
  isLoading,
  onRefresh,
  onSectionSaved,
  previewExport,
  previewIsRegenerating,
  previewLanguage,
  onPreviewLanguageChange,
  onPreviewRegenerate,
}) => {
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [applyingSection, setApplyingSection] = useState<string | null>(null);
  // TASK-406: default to preview-first; "Edit sections" toggle reveals the
  // original section-by-section editor as a power-user fallback.
  const [editMode, setEditMode] = useState(false);
  // Tracks dismissed proposals as "{messageId}:{sectionKey}"
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());

  const activeBriefSection = useMeetingPrepBridge((s) => s.activeBriefSection);
  const setActiveBriefSection = useMeetingPrepBridge((s) => s.setActiveBriefSection);
  // TASK-715: chat lives in the dock's meeting-scope store. Only THIS session's
  // turns count — a restored (persisted) thread must not resurrect old proposals.
  const { account } = useAccount();
  const useMeetingChatStore = useMemo(
    () => getDockChatStore(account?.id ?? 'unknown', { scopeType: 'meeting', entityId: meetingId }),
    [account?.id, meetingId]
  );
  const allMessages = useMeetingChatStore((s) => s.messages);
  const mountLenRef = useRef<number>(-1);
  if (mountLenRef.current === -1) mountLenRef.current = useMeetingChatStore.getState().messages.length;
  const messages = useMemo(
    () => allMessages.slice(mountLenRef.current),
    [allMessages]
  );
  const lastHighlightedRef = useRef<number>(0);

  // Build a map of pending AI proposals: sectionKey -> { item, msgId }
  // Iterates all messages so multiple proposals across turns are visible.
  const pendingUpdates = useMemo(() => {
    const map = new Map<string, { item: BriefUpdateItem; msgId: string }>();
    for (const msg of messages) {
      const briefUpdates = msg.side?.briefUpdates;
      if (msg.role !== 'assistant' || !briefUpdates?.length) continue;
      for (const update of briefUpdates) {
        const dismissKey = `${msg.id}:${update.section}`;
        if (!dismissedKeys.has(dismissKey)) {
          map.set(update.section, { item: update, msgId: msg.id });
        }
      }
    }
    return map;
  }, [messages, dismissedKeys]);

  // Highlight the section whenever a new AI proposal arrives (no auto-save).
  useEffect(() => {
    if (messages.length <= lastHighlightedRef.current) return;
    for (let i = lastHighlightedRef.current; i < messages.length; i++) {
      const msg = messages[i];
      const briefUpdates = msg.side?.briefUpdates;
      if (msg.role === 'assistant' && briefUpdates?.length) {
        const last = briefUpdates[briefUpdates.length - 1];
        setActiveBriefSection(last.section);
      }
    }
    lastHighlightedRef.current = messages.length;
  }, [messages, setActiveBriefSection]);

  // ============================================================================
  // Section extraction
  // ============================================================================

  const getSections = useCallback((): BriefSection[] => {
    if (!briefData) return [];

    const content = selectBrief(briefData).briefContent as Record<string, unknown>;

    const sections: BriefSection[] = [];

    const asText = (v: unknown): string => (typeof v === 'string' ? v : '');

    if (content.executiveSummary) {
      sections.push({
        key: 'executiveSummary',
        title: 'Executive Summary',
        content: String(content.executiveSummary),
      });
    }

    if (content.keyPoints) {
      sections.push({
        key: 'keyPoints',
        title: 'Key Discussion Points',
        content: Array.isArray(content.keyPoints)
          ? (content.keyPoints as string[]).map((p, i) => `${i + 1}. ${p}`).join('\n')
          : asText(content.keyPoints),
      });
    }

    if (content.meetingAgenda) {
      const raw = content.meetingAgenda;
      sections.push({
        key: 'meetingAgenda',
        title: 'Meeting Agenda',
        content: Array.isArray(raw)
          ? (raw as Array<{ title: string; duration?: string; topics?: string[] }>)
              .map((item) => {
                let line = `- ${item.title}`;
                if (item.duration) line += ` (${item.duration})`;
                if (item.topics?.length) line += `\n  ${item.topics.join(', ')}`;
                return line;
              })
              .join('\n')
          : asText(raw),
      });
    }

    if (content.actionItems) {
      const raw = content.actionItems;
      sections.push({
        key: 'actionItems',
        title: 'Action Items',
        content: Array.isArray(raw)
          ? (raw as Array<{ task: string; owner?: string; due?: string }>)
              .map((a) => `- ${a.task}${a.owner ? ` (${a.owner})` : ''}${a.due ? ` — due ${a.due}` : ''}`)
              .join('\n')
          : asText(raw),
      });
    }

    if (content.recommendations) {
      const raw = content.recommendations;
      sections.push({
        key: 'recommendations',
        title: 'Recommendations',
        content: Array.isArray(raw)
          ? (raw as Array<{ title: string; description?: string }>)
              .map((r) => `- **${r.title}**: ${r.description || ''}`)
              .join('\n')
          : asText(raw),
      });
    }

    if (content.comments) {
      sections.push({
        key: 'comments',
        title: 'Comments',
        content: String(content.comments),
      });
    }

    return sections;
  }, [briefData]);

  // ============================================================================
  // Manual editing
  // ============================================================================

  const startEditing = (section: BriefSection) => {
    setEditingSection(section.key);
    setEditValue(section.content);
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditValue('');
  };

  // TASK-413 Bug 3: read the current brief version so we can pass it to the
  // backend as `expected_version`. The server returns 409 if another writer
  // has bumped it since this component loaded.
  const currentBriefVersion = selectBrief(briefData).version;

  const saveSection = async (sectionKey: string) => {
    setSavingSection(sectionKey);
    try {
      await meetingApi.updateBriefSection(meetingId, sectionKey, editValue, currentBriefVersion);
      toast.success('Section updated');
      setEditingSection(null);
      setEditValue('');
      onSectionSaved ? onSectionSaved() : onRefresh();
    } catch (err: unknown) {
      if (err instanceof BriefVersionConflictError) {
        toast.error('Section changed externally — refreshing. Please re-apply your edit.');
        onSectionSaved ? onSectionSaved() : onRefresh();
      } else {
        toast.error('Failed to save section');
      }
    } finally {
      setSavingSection(null);
    }
  };

  // ============================================================================
  // AI proposal actions
  // ============================================================================

  const applyBriefUpdate = async (sectionKey: string) => {
    const pending = pendingUpdates.get(sectionKey);
    if (!pending) return;

    setApplyingSection(sectionKey);
    try {
      await meetingApi.updateBriefSection(
        meetingId,
        sectionKey,
        pending.item.content,
        currentBriefVersion,
      );
      toast.success('Section updated');
      setDismissedKeys((prev) => new Set([...prev, `${pending.msgId}:${sectionKey}`]));
      setActiveBriefSection(sectionKey);
      onSectionSaved?.();
    } catch (err: unknown) {
      if (err instanceof BriefVersionConflictError) {
        toast.error('Section changed externally — refreshing. Re-apply if still relevant.');
        onSectionSaved?.();
      } else {
        toast.error('Failed to apply update');
      }
    } finally {
      setApplyingSection(null);
    }
  };

  const skipBriefUpdate = (sectionKey: string) => {
    const pending = pendingUpdates.get(sectionKey);
    if (!pending) return;
    setDismissedKeys((prev) => new Set([...prev, `${pending.msgId}:${sectionKey}`]));
    setActiveBriefSection(null);
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Generating brief...</p>
        </div>
      </div>
    );
  }

  if (!briefData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500 mb-3">No brief generated yet</p>
          <button
            onClick={onRefresh}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Generate Brief
          </button>
        </div>
      </div>
    );
  }

  const sections = getSections();
  const pendingProposalCount = pendingUpdates.size;
  // TASK-407: use the presigned MinIO URL directly from the response.
  // Going through the gateway /preview redirect breaks under Vite dev because
  // the dev proxy follows the 302 server-side to localhost:9000, which isn't
  // reachable from inside the frontend container.
  const previewPdfUrl =
    previewExport && previewExport.status === 'ready' ? previewExport.pdf_url ?? null : null;
  const pptxDownloadHref =
    previewExport && previewExport.status === 'ready'
      ? previewExport.pptx_url ?? (previewExport.export_id ? getBriefPptxDownloadHref(previewExport.export_id) : null)
      : null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Brief toolbar (TASK-406: preview-first) */}
      <div className="px-6 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">Meeting Brief</h2>
            <p className="text-sm text-gray-500 truncate">
              {clientName} — {meetingType.replace(/_/g, ' ')}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={previewLanguage}
              onChange={(e) => onPreviewLanguageChange(e.target.value as 'en' | 'de' | 'fr')}
              disabled={previewIsRegenerating}
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:border-blue-500"
              aria-label="Deck language"
            >
              <option value="en">EN</option>
              <option value="de">DE</option>
              <option value="fr">FR</option>
            </select>
            <button
              onClick={() => setEditMode((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                editMode
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100 border border-transparent'
              }`}
              aria-pressed={editMode}
            >
              {editMode ? (
                <>
                  <Eye className="w-4 h-4" />
                  Preview
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  Edit sections
                </>
              )}
            </button>
            {pptxDownloadHref && (
              <a
                href={pptxDownloadHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-kielis-navy hover:bg-slate-800 rounded-md transition-colors"
              >
                <Download className="w-4 h-4" />
                Download PPTX
              </a>
            )}
            <button
              onClick={onPreviewRegenerate}
              disabled={previewIsRegenerating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
              title="Regenerate the deck preview"
            >
              <RefreshCw className={`w-4 h-4 ${previewIsRegenerating ? 'animate-spin' : ''}`} />
              Regenerate
            </button>
          </div>
        </div>
        {pendingProposalCount > 0 && !editMode && (
          <div className="mt-2 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
            <span>
              AI proposed {pendingProposalCount} edit{pendingProposalCount > 1 ? 's' : ''}. Switch to
              "Edit sections" to review.
            </span>
          </div>
        )}
        {previewExport?.status === 'failed' && previewExport.error_message && (
          <div className="mt-2 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{previewExport.error_message}</span>
          </div>
        )}
      </div>

      {/* Preview pane (default) OR section editor (edit mode) */}
      {!editMode ? (
        <div className="flex-1 overflow-hidden">
          <BriefDeckPreview
            pdfUrl={previewPdfUrl}
            isRegenerating={previewIsRegenerating}
            emptyState={
              <div className="text-center max-w-sm">
                <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-3">
                  {previewIsRegenerating
                    ? 'Generating deck preview…'
                    : 'No deck generated yet.'}
                </p>
                {!previewIsRegenerating && (
                  <button
                    onClick={onPreviewRegenerate}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    Generate Deck Preview
                  </button>
                )}
              </div>
            }
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {sections.length === 0 && (
          <div className="text-center py-8">
            <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Brief is empty. Ask the AI to generate content.</p>
          </div>
        )}

        {sections.map((section) => {
          const pending = pendingUpdates.get(section.key);

          return (
            <div
              key={section.key}
              className={`rounded-lg border transition-colors ${
                activeBriefSection === section.key
                  ? 'border-blue-300 bg-blue-50/50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              {/* Section header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">{section.title}</h3>
                {editingSection !== section.key && !pending && (
                  <button
                    onClick={() => startEditing(section)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Current content */}
              <div className="px-4 py-3">
                {editingSection === section.key ? (
                  <div className="space-y-2">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={6}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={cancelEditing}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                      >
                        <X className="w-4 h-4 inline mr-1" />
                        Cancel
                      </button>
                      <button
                        onClick={() => saveSection(section.key)}
                        disabled={savingSection === section.key}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:bg-gray-300"
                      >
                        {savingSection === section.key ? (
                          <Loader2 className="w-4 h-4 inline mr-1 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 inline mr-1" />
                        )}
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {section.content}
                  </p>
                )}
              </div>

              {/* AI proposal — shown below current content */}
              {pending && editingSection !== section.key && (
                <div className="mx-4 mb-4 rounded-md border border-blue-200 bg-blue-50">
                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-blue-200">
                    <Sparkles className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-medium text-blue-700">AI Suggestion</span>
                    {pending.item.reason && (
                      <span className="ml-auto text-xs text-blue-500 truncate max-w-[200px]">
                        {pending.item.reason}
                      </span>
                    )}
                  </div>
                  <p className="px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {pending.item.content}
                  </p>
                  <div className="flex items-center gap-2 px-3 py-2 border-t border-blue-200">
                    <button
                      onClick={() => applyBriefUpdate(section.key)}
                      disabled={applyingSection === section.key}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:bg-blue-300 transition-colors"
                    >
                      {applyingSection === section.key ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                      Apply
                    </button>
                    <button
                      onClick={() => skipBriefUpdate(section.key)}
                      disabled={applyingSection === section.key}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Skip
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}
    </div>
  );
};

export default BriefTab;
